// check if the element is already defined
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      // 99B 20250918 -PO, for no-js cards (serverside rendered collection product cards to be removed if JS allowed),
      // don't initialise (as they will just be removed after load if JS enabled)
      if (this.dataset.noJsCard && this.dataset.noJsCard !== 'false') {
        return;
      }
      this.productId = this.dataset.productId;
      this.variantsData = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.init();
    }

    init() {
      this.setupEventListeners();
      this.updateSelectedVariantStock();
    }

    //99B 20260306 -PO, changed to get store ID not name for more reliable mapping, and display visible error
    //if ID missing from store metaobject.
    getPreferredStore() {
      try {
        const storedData = localStorage.getItem('userDeliveryStore');
        if (storedData) {
          const userStore = JSON.parse(storedData);
          const storeReference = userStore?.store_reference;
          if(!storeReference) {
            console.error(`Selected store (${userStore?.name || 'also missing name'}) missing store_reference in metaobject.`);
            return null;
          }
          return storeReference;
        }
      } catch (e) {
        console.error('Error accessing local storage or parsing userDeliveryStore:', e);
      }
      return null;
    }

    parseStockData(stockData) {
      if (!stockData || stockData === 'null') return null;
      return typeof stockData === 'string' ? JSON.parse(stockData) : stockData;
    }

    findPreferredStoreLocation(clickAndCollect, preferredStoreId) {
      if (!clickAndCollect || !Array.isArray(clickAndCollect)) return null;

      return clickAndCollect.find(
        loc => loc.location_id === preferredStoreId
      );
    }

    getStockInfoFromLocation(storeLocation) {
      const result = {
        stockStatus: 5,
        storeName: storeLocation?.name,
        nearbyStoreName: null,
        nearbyStoreDistance: null,
        specialOrderMinDays: null,
        specialOrderMaxDays: null,
      };

      if (!storeLocation || typeof storeLocation.stock_status === 'undefined') {
        return result;
      }

      const status = storeLocation.stock_status;
      const numericStatus = parseInt(status, 10);

      if (isNaN(numericStatus) || numericStatus < 0 || numericStatus > 4) {
        return result;
      }

      result.stockStatus = numericStatus;

      // Nearby store info
      if (numericStatus === 1 && storeLocation.nearest_available_store) {
        const nearby = storeLocation.nearest_available_store;
        if (nearby.name) {
          result.nearbyStoreName = nearby.name;
        }
        if (typeof nearby.distance !== 'undefined') {
          result.nearbyStoreDistance = nearby.distance;
        }
      }

      // Special order info
      if (
        numericStatus === 3 &&
        storeLocation.transfer_time &&
        Array.isArray(storeLocation.transfer_time) &&
        storeLocation.transfer_time.length >= 2
      ) {
        result.specialOrderMinDays = storeLocation.transfer_time[0];
        result.specialOrderMaxDays = storeLocation.transfer_time[1];
      }

      return result;
    }

    clearStockStatus() {
      const statusElement = this.querySelector('[data-click-and-collect-status]');
      if (statusElement) {
        statusElement.className =
          'product-card__preferred-stock-status twp-stock-status text-sm mt-2 flex items-start gap-1';
        const statusTextElement = statusElement.querySelector('.twp-status-text');
        if (statusTextElement) statusTextElement.textContent = '';
      }
    }

    setupEventListeners() {
      //99B 20251009 -PO, selected variant scrolls into view when selected when click or via chose you size
      const variantButtons = this.querySelectorAll('.variant-buttons__item');
      variantButtons.forEach(button => {
        const input = button.querySelector('input[type="radio"]');
        if (!input) {
          console.warn(`Failed to setup variant stock status update event listeners for productId: ${this.dataset.productId}`);
          return;
        }
        input.addEventListener('change', () => {
          //99B 20251013 -PO, enable scroll into view only on Edge and Chrome - issues found moving user down page with FYS on Safari and Firefox
          if (navigator.userAgent.includes("Chrome") || navigator.userAgent.includes("Edge")) {
            button.scrollIntoView({behavior: "smooth", container: "nearest", block: "nearest", inline: "center"});
          }
          const variantId = input.value;
          this.updateStockStatusForVariant(variantId);
        });
      });

      //99B 20250929 -PO, setup events tracking for product link clicks, and add to cart
      const productLinks = this.querySelectorAll('[data-product-link]');

      productLinks.forEach(link => {
        link.addEventListener('click', e => {
          this.algoliaTrackingProductClicked();
        })
      });

      const addToCart = this.querySelectorAll('.product-card__submit');
      addToCart.forEach(submitButton => {
        submitButton.addEventListener('click', e => {
          if (submitButton.classList.contains("disabled")) {
            return;
          };
          this.algoliaTrackingAddToCart();
        })
      });

      FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
        this.updateSelectedVariantStock.bind(this)
      );

      //99B 20250910 -PO, setup card to be clickable, but not containing interactive elements
      const productUrl = this.dataset.productUrl;
      if (!productUrl) {
        return;
      }

      this.addEventListener('click', (event) => {
        this.algoliaTrackingProductClicked();
        const selectedVariantInput = this.querySelector(
          '.variant-buttons__item input[type="radio"]:checked'
        );
        window.location.href = `${productUrl}${selectedVariantInput ? `?variant=${selectedVariantInput.value}` : ''}`;
      })

      const clickableElements = this.querySelectorAll('button, a, .product-card__variant-selector, .product-card__compare, .ruk_rating_snippet_card');
      clickableElements.forEach(input => {
        input.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      })
    }

    updateStockStatusForVariant(variantId) {
      const productLinks = this.querySelectorAll('[data-product-link]');
      const singleVariant = this.variantsData.length === 1;

      if (productLinks.length > 0) {
        productLinks.forEach(link => {
          link.href = `${this.dataset.productUrl}${singleVariant ? '' : `?variant=${variantId}`}`;
        });
      }

      // Update badges for the selected variant
      this.updateBadgesForVariant(variantId);

      const preferredStoreId = this.getPreferredStore();
      if (!preferredStoreId) {
        this.clearStockStatus();
        this.updateComingSoonBadge(4);
        return;
      }

      const selectedVariant = this.variantsData.find(variant => variant.id == variantId);
      if (!selectedVariant) return;

      const stockData = this.parseStockData(selectedVariant.stockData);

      const shippingData = selectedVariant.shippingData;

      if (!stockData) {
        this.clearStockStatus();
        this.updateComingSoonBadge(4);
        return;
      }

      const storeLocation = this.findPreferredStoreLocation(stockData, preferredStoreId);

      if (!storeLocation) {
        // 99B 20250822 -PO, previously not displaying delivery availability if store location not found
        this.updateStatusDisplay(
          4,
          null,
          null,
          null,
          null,
          null,
          shippingData
        );
        this.updateComingSoonBadge(4);
        return;
      }

      const info = this.getStockInfoFromLocation(storeLocation);
      this.updateStatusDisplay(
        info.stockStatus,
        info.storeName,
        info.nearbyStoreName,
        info.nearbyStoreDistance,
        info.specialOrderMinDays,
        info.specialOrderMaxDays,
        shippingData
      );

      this.updateComingSoonBadge(info.stockStatus);
    }

    //99B 20251009 -PO, add Algolia click event to product card
    algoliaTrackingProductClicked() {
      if (!this.dataset.queryId || !this.dataset.index || typeof algoliaSearchInsights !== 'function') return;

      algoliaSearchInsights("clickedObjectIDsAfterSearch", {
        eventName: "Product Clicked",
        index: "shopify_products",
        queryID: this.dataset.queryId,
        objectIDs: [this.dataset.objectId],
        positions: [parseInt(this.dataset.index)]
      });

      //99B 20251015 -PO, add attach query id to product id (tracking data if user clicks add to cart after reaching page)
      let productsClicked = JSON.parse(sessionStorage.getItem("algoliaProductsClicked"));
      if (!productsClicked) productsClicked = [];
      const existingProduct = productsClicked.find(event => event.productId === this.dataset.productId);
      if (existingProduct) {
        existingProduct.queryId = this.dataset.queryId;
      }else{
        productsClicked.push({
          'productId' : this.dataset.productId,
          'queryId' : this.dataset.queryId
        });
      }
      sessionStorage.setItem("algoliaProductsClicked",JSON.stringify(productsClicked));
    }

    algoliaTrackingAddToCart() {
      if (!this.dataset.queryId || typeof algoliaSearchInsights !== 'function') return;

      //99B 20251009 -PO, changing objectID to variant instead of product id, and getting price and discount
      const productCardForm = this.querySelector('form.product-card__form');
      if (!productCardForm || !productCardForm.dataset.variantsJson) {
        console.warn("Algolia insights failed to submit event - cannot find product card form variants data.");
        return;
      }
      const variantsData = JSON.parse(productCardForm.dataset.variantsJson);
      if (variantsData.length <= 0) {
        console.warn("Algolia insights failed to submit event - variants data empty.");
        return;
      }
      let variantData = variantsData[0]
      if (variantsData.length > 1) {
        //find the selected variant
        const selectedVariantInput = productCardForm.querySelector(
        '.variant-buttons__item input[type="radio"]:checked'
        );
        if (!selectedVariantInput) {
          console.warn("Algolia insights failed to submit event - selected variant not found.");
          return;
        }
        variantData = variantsData.find(data => data.id === selectedVariantInput.value)
        if (!variantData) {
          console.warn("Algolia insights failed to submit event - cannot find selected variant match in variant data.");
          return;
        }
      }

      algoliaSearchInsights("addedToCartObjectIDsAfterSearch", {
        eventName: "Product Added To Cart",
        index: "shopify_products",
        queryID: this.dataset.queryId,
        objectIDs: [variantData.id],
        objectData: [{
          price: Math.round(variantData.price)/100,
          discount: variantData.compare_at_price ? Math.round(variantData.compare_at_price - variantData.price)/100 : 0,
          quantity: 1
        }],
        currency: "AUD",
        value: Math.round(variantData.price)/100
      });

      //99B 20251016 -PO, add attach query id to variant id (tracking data if user purchases product after adding to cart)
      window.AlgoliaTracking.trackSearchAttribution(this.dataset.queryId,variantData.id,this.dataset.productId);
    }

    updateStatusDisplay(
      stockStatus,
      storeName,
      nearbyStoreName,
      nearbyStoreDistance,
      specialOrderMinDays,
      specialOrderMaxDays,
      shippingData
    ) {
      const statusTextMap = {
        0: this.dataset.textAvailable,
        1: this.dataset.textAvailableNearby,
        2: this.dataset.textComingSoon,
        3: this.dataset.textSpecialOrder,
      };

      const statusElement = this.querySelector('[data-click-and-collect-status]');
      const statusTextElement = statusElement
        ? statusElement.querySelector('.twp-status-text')
        : null;

      if (statusElement && statusTextElement) {
        let statusText = '';
        if (stockStatus <= 4 && statusTextMap.hasOwnProperty(stockStatus)) {
          if (stockStatus === 1 && nearbyStoreName) {
            statusText = `Available nearby at ${nearbyStoreName}`;
            if (nearbyStoreDistance !== null) {
              statusText += ` (${nearbyStoreDistance}km)`;
            }
          } else if (
            stockStatus === 3 &&
            specialOrderMinDays !== null &&
            specialOrderMaxDays !== null
          ) {
            statusText = `Special order (ready in ${specialOrderMinDays}-${specialOrderMaxDays} days)`;
          } else if (stockStatus === 0) {
            if (storeName) {
              statusText = this.dataset.textAvailable.replace('{{ store_name }}',storeName);
            } else {
              statusText = 'In Stock';
            }
          } else {
            statusText = statusTextMap[stockStatus];
          }

        } else {
          //99B 20250822 -PO display unavailable message for C&C instead of blank
          if (storeName) {
            statusText = `Not available at ${storeName}`;
          } else {
            statusText = 'Not available for pickup';
          }
        }

        statusElement.className = `product-card__preferred-stock-status twp-stock-status text-sm mt-2 flex items-start gap-1 status-${stockStatus}`;
        statusTextElement.textContent = statusText;
      }

      // The below code updates the product card delivery status based on the variants quantities_available field.
      // if both express shipping (quantities_available[1]) and standard shipping (quantities_available[0]) are 0, we know the product is not available for delivery.
      const deliveryStatusElement = this.querySelector('[data-delivery-status]');
      const deliveryStatusTextElement = deliveryStatusElement
        ? deliveryStatusElement.querySelector('.twp-status-text')
        : null;

      if (deliveryStatusElement && deliveryStatusTextElement) {
        let quantities_available;

        try {
          quantities_available = JSON.parse(shippingData);
        } catch (e) {
          console.error('Error parsing shipping data:', e);
          quantities_available = null;
        }

        if (!quantities_available) return;
        const standardShipping = quantities_available[0];
        const expressShipping = quantities_available[1];

        if (standardShipping > 0 || expressShipping > 0) {
          deliveryStatusElement.className = `product-card__preferred-stock-status twp-stock-status text-sm mt-2 flex items-start gap-1 status-0`;
          deliveryStatusTextElement.textContent = window.theme.strings.deliveryStatus.available;
        } else {
          deliveryStatusElement.className = `product-card__preferred-stock-status twp-stock-status text-sm mt-2 flex items-start gap-1 status-4`;
          deliveryStatusTextElement.textContent = window.theme.strings.deliveryStatus.notAvailable;
        }
      }
    }

    updateSelectedVariantStock() {
      if (this.variantsData.length < 2) {
        this.updateStockStatusForVariant(this.variantsData[0].id);
      }

      const selectedVariantInput = this.querySelector(
        '.variant-buttons__item input[type="radio"]:checked'
      );
      if (selectedVariantInput) {
        this.updateStockStatusForVariant(selectedVariantInput.value);
      } else {
        this.updateComingSoonBadgeUnselected();
      }
    }

    updateBadgesForVariant(variantId) {
      const badgeContainer = this.querySelector('.product-card__badge');
      if (!badgeContainer) return;

      try {
        // 99B 20260225 -PO, hide any existing variant badges, and unhide selected
        const existingVariantBadges = [...badgeContainer.querySelectorAll('.f-badge[badge-variant]')];
        existingVariantBadges.forEach(badge => badge.style.display = 'none');

        const selectedVariantBadge = existingVariantBadges.find(badge => badge.getAttribute('badge-variant') === variantId);
        if (selectedVariantBadge) selectedVariantBadge.style.display = '';
        
        // Sold out badge
        if (window.theme.showSoldOutBadge) {
          // Find the selected variant button
          const selectedVariantItem = this.querySelector(
            `.variant-buttons__item input[value="${variantId}"]`
          )?.closest('.variant-buttons__item');
          if (!selectedVariantItem) return;

          const variantInput = selectedVariantItem.querySelector('input[type="radio"]');
          const isAvailable = variantInput?.dataset.variantAvailable === 'true';
          const soldOutBadge = badgeContainer.querySelector('.f-badge--soldout')

          if (!isAvailable && !soldOutBadge) {
            badgeContainer.insertAdjacentHTML('beforeend','<span class="f-badge f-badge--soldout" aria-hidden="true">Sold out</span>');
          } else if (isAvailable && soldOutBadge) {
            // 99B 20251103 -PO, mechanism to remove sold out badge 
            // (if FYS component selected sold out variant, switching to in-stock variant badge would remain)
            soldOutBadge.remove();
          }
        }
        
      } catch (error) {
        console.error('Error modifying variant badges: ', error);
      }
    }

    /**
     * 99B 20260225 -PO, For multi-variant product, where no variant is selected, determines if all variants
     * are pre-order at the selected location, and if they are, will add the coming soon badge.
     */
    updateComingSoonBadgeUnselected() {
      const preferredStoreId = this.getPreferredStore();
      if (!preferredStoreId) {
        this.updateComingSoonBadge(4);
      }

      const variantsStockStatus = this.variantsData.map(variant => 
        this.getStockInfoFromLocation(this.findPreferredStoreLocation(this.parseStockData(variant.stockData),preferredStoreId))?.stockStatus);

      this.updateComingSoonBadge(variantsStockStatus?.every(status => status === 2) ? 2 : 4);
    }

    /**
     *  99B 20260225 -PO, Display coming soon badge if preorder (stock status set to 2)
     */
    updateComingSoonBadge(status) {
      const comingSoonBadge = this.querySelector('[data-coming-soon-badge]');
      if (!comingSoonBadge) return;

      const isPreorder = status === 2;
      comingSoonBadge?.classList.toggle('hidden', !isPreorder);
    }

    /**
     * Convert string to handle format (lowercase, replace spaces/special chars with hyphens)
     */
    handleize(str) {
      return str
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    }
  }
  customElements.define('product-card', ProductCard);
}
